import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Check, Dices } from 'lucide-react';
import { usernameProblem, USERNAME_MAX } from '../../shared/usernames.ts';
import { checkUsername, saveUsername, suggestUsername, useAuth } from '../app/auth.ts';
import { usePreferences } from '../app/preferences.ts';

type Availability = 'unknown' | 'checking' | 'available' | 'taken' | 'invalid';

/** First sign-in: pick a unique username, starting from a random free one. */
export function WelcomePage() {
  const { prefs, t } = usePreferences();
  const { setUser } = useAuth();
  const [name, setName] = useState('');
  const [availability, setAvailability] = useState<Availability>('unknown');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applySuggestion = useCallback((next: string | null) => {
    if (!next) return;
    setName(next);
    setAvailability('available');
    setError(null);
  }, []);
  const suggest = () => suggestUsername(prefs.locale).then(applySuggestion);

  // Start with a random name that nobody has.
  useEffect(() => {
    let alive = true;
    suggestUsername(prefs.locale).then((next) => {
      if (alive) applySuggestion(next);
    });
    return () => {
      alive = false;
    };
  }, [prefs.locale, applySuggestion]);

  // A name that breaks the rules is shown as such without asking the server.
  const invalid = name !== '' && usernameProblem(name) !== null;
  const shown: Availability = invalid ? 'invalid' : availability;

  // Check the typed name a moment after the child stops typing.
  useEffect(() => {
    if (!name || invalid || availability !== 'checking') return;
    const id = window.setTimeout(async () => {
      try {
        const result = await checkUsername(name);
        setAvailability(result.available ? 'available' : result.problem ? 'invalid' : 'taken');
      } catch {
        setAvailability('unknown');
      }
    }, 350);
    return () => window.clearTimeout(id);
  }, [name, invalid, availability]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (usernameProblem(name)) {
      setAvailability('invalid');
      return;
    }
    setSaving(true);
    const result = await saveUsername(name);
    setSaving(false);
    if (result.ok) {
      setUser(result.user);
      return;
    }
    if (result.error === 'username_taken') setAvailability('taken');
    else if (result.error === 'username_invalid') setAvailability('invalid');
    else setError(t('error.generic'));
  };

  const hint =
    shown === 'available'
      ? { text: t('welcome.available'), cls: 'ok' }
      : shown === 'taken'
        ? { text: t('welcome.taken'), cls: 'bad' }
        : shown === 'invalid'
          ? { text: t('welcome.invalid'), cls: 'bad' }
          : shown === 'checking'
            ? { text: t('welcome.checking'), cls: '' }
            : { text: t('welcome.rules'), cls: '' };

  return (
    <div className="page-fill">
      <div className="lobby-step">
        <form className="card lobby-card welcome-card pop-in" onSubmit={onSubmit}>
          <div className="welcome-head">
            <h1 className="page-title">{t('welcome.title')}</h1>
            <p className="lobby-card-desc">{t('welcome.lead')}</p>
          </div>

          <div className="field">
            <label className="field-label" htmlFor="username">
              {t('welcome.label')}
            </label>
            <div className="username-row">
              <input
                id="username"
                className="input"
                value={name}
                maxLength={USERNAME_MAX}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                aria-invalid={shown === 'taken' || shown === 'invalid' ? true : undefined}
                aria-describedby="username-hint"
                onChange={(e) => {
                  setName(e.target.value.replace(/\s/g, ''));
                  setAvailability('checking');
                  setError(null);
                }}
              />
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void suggest()}
                aria-label={t('welcome.another')}
                title={t('welcome.another')}
              >
                <Dices size={26} />
              </button>
            </div>
            <p id="username-hint" className={`username-hint ${hint.cls}`} role="status">
              {hint.text}
            </p>
          </div>

          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}

          <p className="welcome-note">{t('welcome.note')}</p>

          <button
            type="submit"
            className="btn btn-green btn-lg btn-block"
            disabled={saving || shown === 'taken' || shown === 'invalid' || shown === 'checking' || !name}
          >
            {saving ? <span className="spinner" style={{ width: 26, height: 26 }} /> : <Check size={28} strokeWidth={3} />}
            {t('welcome.save')}
          </button>
        </form>
      </div>
    </div>
  );
}
